<template>
  <family-page-wrapper>
    <card-container
      :id="pageId"
      :is-loading="isLoading"
      loading-message="Sending Contact Info..."
      :disabled="isLoading || !isPhoneNumberValid || !isEmailValid"
      @submit="submitForm"
      @reset="resetForm"
    >
      <template #title>
        Contact Us
      </template>

      <template #content>
        <div class="row align-items-center">
          <label class="col-sm-3 control-label"> Name* </label>
          <div class="col-sm-7">
            <sanitized-input
              id="customerName"
              v-model="formData.name"
              required
            />
          </div>
        </div>

        <div class="row align-items-center">
          <label class="col-sm-3 control-label"> Phone Number* </label>
          <phone-number-input
            v-model="formData.phoneNumber"
            class="col-sm-7"
            :required="true"
          />
        </div>

        <div class="row">
          <label class="col-sm-3 control-label pt-10"> Email* </label>
          <div class="col-sm-7">
            <email-input
              v-model="formData.email"
              required
              @update-validation="updateEmailValidation"
            />
          </div>
        </div>

        <div class="row">
          <label class="col-sm-3 control-label pt-10"> Best Contact Time* </label>
          <div class="col-sm-7">
            <date-picker
              v-model="formData.bestCallbackTime"
              type="datetime"
              placeholder="Thu, 10-28-2021 12:00 AM"
              format="ddd, MM-DD-YYYY hh:mm A"
              :disabled-date="disabledDate"
              :required="true"
            >
              <template #comment>
                <span class="help-block"> Est time - Thu, 10-28-2021 12:00 AM </span>
              </template>
            </date-picker>
          </div>
        </div>

        <div class="row">
          <label class="col-sm-3 control-label pt-5 mobile-pt-0"> Details* </label>
          <div class="col-sm-7">
            <sanitized-textarea
              id="details"
              v-model="formData.details"
              autosize
              required
            />
          </div>
        </div>
      </template>
    </card-container>
  </family-page-wrapper>
</template>

<script setup lang="ts">
import { ref, reactive, computed, watch } from 'vue';
import moment from 'moment-timezone';

import util from '@/Utils/util';
import { useCustomer } from '@/use/customer';
import { useSwalDialog } from '@/use/swalDialog';
import { defaultTimeZone } from '@/constants/app';
import { emailService } from '@/services/email';

const pageId = 'contact-us';

const { customerPhoneNumber, customerInfo, customerFullName } = useCustomer();

const { showSuccessPopupDialog, showErrorPopupDialog } = useSwalDialog();

const formData = reactive({
  name: '',
  phoneNumber: '',
  email: '',
  bestCallbackTime: '',
  details: '',
});
const isEmailValid = ref(true);
const isLoading = ref(false);

const isPhoneNumberValid = computed(() => util.isPhoneNumberValid(formData.phoneNumber));

const disabledDate = (date: Date) => {
  const dateLocalMoment = moment(date);
  const startOfTodayDefaultTimeZoneMoment = moment.tz(customerInfo.value.timeZone || defaultTimeZone).startOf('day');
  const startOfTodayLocalMoment = moment(startOfTodayDefaultTimeZoneMoment.format('YYYY-MM-DD hh:mm'));
  return dateLocalMoment.isBefore(startOfTodayLocalMoment, 'day');
};

const updateEmailValidation = (value: boolean) => {
  isEmailValid.value = value;
};

const submitForm = async () => {
  isLoading.value = true;
  try {
    await emailService.sendContactUsEmail({
      name: formData.name,
      phoneNumber: formData.phoneNumber,
      email: formData.email,
      bestCallbackTime: util.formatDateTime(formData.bestCallbackTime, 'ddd, MM-DD-YYYY hh:mm A'),
      details: formData.details,
    });

    showSuccessPopupDialog({
      text: 'The contact information has been sent successfully.',
    });
  } catch (error) {
    const errorText = util.getGraphqlError(error, false);
    showErrorPopupDialog({
      text: errorText,
    });
  }
  isLoading.value = false;
};

const resetForm = () => {
  formData.name = customerFullName.value || '';
  formData.phoneNumber = customerPhoneNumber.value || '';
  formData.email = customerInfo.value?.email || '';
  formData.bestCallbackTime = '';
  formData.details = '';
};

watch(customerInfo, resetForm, {
  immediate: true,
});
</script>
